/**
 * ProductCard Component - Story 7.1: Create Marketplace Homepage
 * Reusable product card for displaying product information in grid layout
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, MapPin, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProductCardProps } from '../types/publicMarketplace.types';
import { CATEGORY_BADGE_MAP } from '../types/publicMarketplace.types';

/**
 * ProductCard Component
 * Displays product information in a card layout with image, name, category, price, and location
 *
 * @component
 * @example
 * <ProductCard
 *   product={product}
 *   onProductClick={(id) => navigate(`/products/${id}`)}
 * />
 */
export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onProductClick,
  className,
}) => {
  const categoryConfig = CATEGORY_BADGE_MAP[product.category];
  const primaryImage = product.images.find((img) => img.isPrimary) || product.images[0];

  const handleCardClick = () => {
    if (onProductClick) {
      onProductClick(product.id);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onProductClick) {
      e.preventDefault();
      onProductClick(product.id);
    }
  };

  return (
    <Card
      className={cn(
        'group cursor-pointer overflow-hidden transition-all duration-300',
        'hover:shadow-lg hover:scale-105 focus-within:ring-2 focus-within:ring-primary',
        className
      )}
      onClick={handleCardClick}
      onKeyPress={handleKeyPress}
      tabIndex={0}
      role="article"
      aria-label={`Product: ${product.name}`}
    >
      {/* Product Image */}
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        {primaryImage ? (
          <img
            src={primaryImage.url}
            alt={primaryImage.altText || product.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl text-gray-400">📦</span>
          </div>
        )}

        {/* Category Badge - Overlay */}
        <div className="absolute left-3 top-3">
          <Badge
            className={cn(
              'text-xs font-medium shadow-sm',
              categoryConfig.bgColor,
              categoryConfig.color
            )}
          >
            {categoryConfig.label}
          </Badge>
        </div>

        {/* Quick View Button - Overlay (appears on hover) */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            onClick={(e) => {
              e.stopPropagation();
              if (onProductClick) {
                onProductClick(product.id);
              }
            }}
          >
            <Eye className="h-4 w-4" />
            Quick View
          </Button>
        </div>
      </div>

      <CardContent className="p-4">
        {/* Product Name */}
        <h3
          className="mb-2 line-clamp-2 text-base font-semibold text-gray-900"
          title={product.name}
        >
          {product.name}
        </h3>

        {/* Price and Unit */}
        <div className="mb-3 flex items-baseline gap-1">
          <span className="text-2xl font-bold text-primary">
            ₹{(product.price ?? 0).toLocaleString('en-IN')}
          </span>
          <span className="text-sm text-gray-500">/ {product.unit}</span>
        </div>

        {/* Minimum Order Quantity Badge */}
        {product.minimumOrderQuantity && (
          <div className="mb-3 flex items-center gap-1">
            <Badge variant="secondary" className="text-xs">
              <Package className="h-3 w-3 mr-1" />
              Min: {product.minimumOrderQuantity} {product.unit}
            </Badge>
          </div>
        )}

        {/* Location */}
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate" title={`${product.location.district}, ${product.location.state}`}>
            {product.location.district}, {product.location.state}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
